import * as THREE from 'three'
import { Section } from './Section.js'
import { LanternLoader } from './lanterns/LanternLoader.js'
import { LanternPhysicsManager } from './lanterns/LanternPhysicsManager.js'
import { LanternInteractionManager } from './lanterns/LanternInteractionManager.js'

const textureLoader = new THREE.TextureLoader()

// Change this number to shuffle the lantern color pattern
const LANTERN_COLOR_SEED = 8

function seededRandom(seed, index) {
  const x = Math.sin(seed * 9999 + index * 9973) * 10000
  return x - Math.floor(x)
}

export class HeroSection extends Section {
  build(camera, renderer) {
    super.build(camera, renderer)

    this.createTexturedBackground()
    this.createSidePlanes()
    this.createTextPlane()

    this.lanternPivots = []
    this.lanternMeshes = []
    this.lanternTemplates = null
    this.physics = null
    this.interaction = null
    this.camera = null
    this.canvas = null
  }

  setCamera(camera, canvas) {
    this.camera = camera
    this.canvas = canvas
  }

  async initLanterns() {
    const loader = new LanternLoader()
    const { ropeGroup, anchors, lanternTemplates, maskTextures } = await loader.load(
      '/sect01/rope_anchors.glb',
      '/sect01/lantern_model.glb'
    )

    this.lanternTemplates = lanternTemplates
    this.maskTextures = maskTextures

    const contentGroup = new THREE.Group()
    contentGroup.scale.set(2, 2, 2)
    this.add(contentGroup)

    contentGroup.add(ropeGroup)

    for (let i = 0; i < anchors.length; i++) {
      const anchor = anchors[i]
      const pivot = new THREE.Object3D()
      pivot.position.copy(anchor.position)
      pivot.quaternion.copy(anchor.quaternion)
      contentGroup.add(pivot)

      const rand = seededRandom(LANTERN_COLOR_SEED, i)
      const templateIndex = Math.floor(rand * this.lanternTemplates.length)
      const template = this.lanternTemplates[templateIndex]
      const lantern = template.clone()

      // Randomize mask texture on each load
      const maskIndex = Math.floor(Math.random() * this.maskTextures.length)
      lantern.traverse((child) => {
        if (child.isMesh && child.name === 'lantern') {
          child.material.emissiveMap = this.maskTextures[maskIndex]
          child.material.needsUpdate = true
        }
      })

      pivot.add(lantern)

      this.lanternPivots.push(pivot)
      this.lanternMeshes.push(lantern)
    }

    console.log(`Spawned ${this.lanternPivots.length} lanterns`)

    this.physics = new LanternPhysicsManager(this.lanternPivots)

    if (this.camera && this.canvas) {
      this.interaction = new LanternInteractionManager(
        this.camera,
        this.canvas,
        this.lanternMeshes,
        (index) => this.physics.applyImpulse(index, 0.05),
        this.physics
      )
    }
  }

  createTexturedBackground() {
    // Lock background plane to 900:1440 aspect ratio
    const targetAspect = 1440 / 900
    const aspect = window.innerWidth / window.innerHeight

    // Calculate dimensions to cover the viewport while maintaining aspect ratio
    const scale = 1.0
    let width, height
    if (aspect > targetAspect) {
      // Viewport is wider than image
      width = 20 * aspect * scale
      height = width / targetAspect
    } else {
      // Viewport is taller than image
      height = 20 * scale
      width = height * targetAspect
    }

    const geometry = new THREE.PlaneGeometry(width, height)
    this.bgMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x040809),
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide
    })

    this.backgroundPlane = new THREE.Mesh(geometry, this.bgMaterial)
    this.scene.add(this.backgroundPlane)
  }

  createSidePlanes() {
    // Dimensions: 896x1152 pixels, scaled to 85%
    const scale = 0.85
    const aspect = 896 / 1152
    const height = 10.5 * scale
    const width = height * aspect

    // Left plane texture
    const leftTexture = textureLoader.load('/sect01/guardian_L.webp')
    leftTexture.colorSpace = THREE.SRGBColorSpace

    const leftGeometry = new THREE.PlaneGeometry(width, height)
    const leftMaterial = new THREE.MeshBasicMaterial({
      map: leftTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    })

    this.leftPlane = new THREE.Mesh(leftGeometry, leftMaterial)
    this.leftPlane.position.z = 0
    // Position 40px from left edge (convert pixels to world units)
    // height = 10.5 world units = 1152 pixels, so 40px = 40/1152 * 10.5 ≈ 0.365 world units
    const pixelOffset = (40 / 1152) * height
    this.leftPlane.position.x = -width / 2 - pixelOffset
    this.add(this.leftPlane)

    // Right plane texture
    const rightTexture = textureLoader.load('/sect01/guardian_R.webp')
    rightTexture.colorSpace = THREE.SRGBColorSpace

    const rightGeometry = new THREE.PlaneGeometry(width, height)
    const rightMaterial = new THREE.MeshBasicMaterial({
      map: rightTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    })

    this.rightPlane = new THREE.Mesh(rightGeometry, rightMaterial)
    this.rightPlane.position.z = 0
    // Position 40px from right edge
    this.rightPlane.position.x = width / 2 + pixelOffset
    this.add(this.rightPlane)
  }

  createTextPlane() {
    // Dimensions: 273x630 pixels
    const targetHeight = 12.0
    const aspect = 273 / 630
    const width = targetHeight * aspect
    const height = targetHeight

    const texture = textureLoader.load('/sect01/sect01-header.webp')
    texture.colorSpace = THREE.SRGBColorSpace

    const geometry = new THREE.PlaneGeometry(width, height)
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    })

    this.textPlane = new THREE.Mesh(geometry, material)
    this.textPlaneTargetY = 0
    this.textPlaneStartY = 16
    this.textPlane.position.set(0, this.textPlaneTargetY, -1.5)
    this.textPlaneAnimProgress = 0
    this.textPlaneAnimated = false
    this.isExiting = false
    this.add(this.textPlane)
  }

  onResize() {
    if (!this.backgroundPlane) return

    const targetAspect = 1440 / 900
    const aspect = window.innerWidth / window.innerHeight

    const scale = 1.0
    let width, height
    if (aspect > targetAspect) {
      width = 20 * aspect * scale
      height = width / targetAspect
    } else {
      height = 20 * scale
      width = height * targetAspect
    }

    this.backgroundPlane.geometry.dispose()
    this.backgroundPlane.geometry = new THREE.PlaneGeometry(width, height)
  }

  onEnter() {
    super.onEnter()
    this.isExiting = false
    if (this.textPlane) {
      this.textPlane.position.set(0, this.textPlaneTargetY, -1.5)
    }
    if (this.interaction) {
      this.interaction.setEnabled(true)
    }
  }

  update(delta, scrollProgress, cameraZ) {
    // Animate text plane up as soon as camera scrolls below hero position (z=5)
    if (this.textPlane) {
      const scrollDelta = 5 - cameraZ
      const t = Math.min(Math.max(scrollDelta / 20, 0), 1)
      const ease = 1 - Math.pow(1 - t, 3)
      this.textPlane.position.y = this.textPlaneTargetY + (this.textPlaneStartY - this.textPlaneTargetY) * ease
    }

    if (!this.isActive && !this.isExiting) return

    if (this.physics) {
      this.physics.update()
    }
  }

  onExit() {
    super.onExit()
    this.isExiting = true
    if (this.interaction) {
      this.interaction.setEnabled(false)
    }
  }

  destroy() {
    if (this.interaction) {
      this.interaction.destroy()
      this.interaction = null
    }

    if (this.physics) {
      this.physics.destroy()
      this.physics = null
    }

    if (this.lanternTemplates) {
      const disposedGeometries = new Set()
      const disposedMaterials = new Set()
      const disposedTextures = new Set()
      this.lanternTemplates.forEach((template) => {
        template.traverse((child) => {
          if (child.isMesh) {
            if (child.geometry && !disposedGeometries.has(child.geometry)) {
              child.geometry.dispose()
              disposedGeometries.add(child.geometry)
            }
            if (child.material && !disposedMaterials.has(child.material)) {
              if (child.material.map && !disposedTextures.has(child.material.map)) {
                child.material.map.dispose()
                disposedTextures.add(child.material.map)
              }
              child.material.dispose()
              disposedMaterials.add(child.material)
            }
          }
        })
      })
      this.lanternTemplates = null
    }

    if (this.maskTextures) {
      this.maskTextures.forEach((tex) => tex.dispose())
      this.maskTextures = null
    }

    if (this.leftPlane) {
      if (this.leftPlane.material.map) this.leftPlane.material.map.dispose()
      this.leftPlane = null
    }

    if (this.rightPlane) {
      if (this.rightPlane.material.map) this.rightPlane.material.map.dispose()
      this.rightPlane = null
    }

    if (this.textPlane) {
      if (this.textPlane.parent) this.textPlane.parent.remove(this.textPlane)
      if (this.textPlane.geometry) this.textPlane.geometry.dispose()
      if (this.textPlane.material) {
        if (this.textPlane.material.map) this.textPlane.material.map.dispose()
        this.textPlane.material.dispose()
      }
      this.textPlane = null
    }

    if (this.backgroundPlane) {
      if (this.backgroundPlane.geometry) this.backgroundPlane.geometry.dispose()
      if (this.bgMaterial) this.bgMaterial.dispose()
    }

    this.lanternPivots = []
    this.lanternMeshes = []
    this.camera = null
    this.canvas = null

    super.destroy()
  }
}
